export const applyCustomerPreviewAccess = (items = [], isCustomerPreview = false) => (
  isCustomerPreview ? items.map(item => ({ ...item, access: true })) : items
);

const normalizedUsername = (value) => String(value || '').trim().toLowerCase();

// The live hub preview (PreviewBar chooser, Customer/Not a customer
// simulation, read-only write guards, simulated checkout, hub link card) is
// gated to any creator viewing their OWN hub. Server-confirmed ownership is
// the only gate: `data.is_me` must come back true from the server, and the
// authenticated user must match the hub being viewed. URL parameters alone
// can never enable this — they are never inputs to this check.
export const isHubPreviewOwner = (data) => {
  const currentUser = normalizedUsername(data?.current_user);
  const targetInfluencer = normalizedUsername(data?.influencer_info?.influencer);
  return (
    data?.is_me === true
    && currentUser !== ''
    && currentUser === targetInfluencer
  );
};

// Query parameters that open a specific product, tab or checkout. Inside an
// owner preview, Back-to-home clears exactly these and keeps everything else,
// so the simulated audience and device survive the trip back.
//
// `page` is the main one: it is the query param every storefront tab reads
// (searchParams.get("page")) to decide which product view to render
// (community, 1v1coaching, messages, digital_products, courses, ...).
// Without it here, Back-to-home left `page=community` (etc.) in the URL, so a
// refresh after Back reopened the product instead of showing the hub home.
// Dropping it is safe: the hub home is simply what renders when `page` is
// absent (the same fallback already used for `page=home` and for a first-time
// visit with no `page` param at all), so it is never itself keyed to a
// preserved `page` value inside preview.
const PRODUCT_ROUTE_PARAMS = [
  'page', 'tab_id', 'id', 'post_id', 'product_id', 'product_type', 'passive_id',
  'coaching_member', 'duration',
  'open_community', 'open_collection', 'open_membership', 'open_sub',
  'open_coaching', 'open_messages', 'open_digital_products', 'open_product',
  'open_community_checkout', 'open_coaching_checkout',
  'open_message_checkout', 'open_url_checkout', 'open_checkout',
  'open_buy_now_checkout', 'open_buy_collection_checkout', 'open_tip_checkout',
  'open_url_product', 'open_lead_capture', 'open_purchased_url',
  'view_membership', 'single_tier', 'tier', 'billing',
];

// One-shot parameter: the preview chooser must not reopen on Back.
const PREVIEW_ENTRY_PARAM = 'previewEntry';

export const getProductBackSearchParams = (currentSearch, preservePreviewContext = false) => {
  const current = currentSearch instanceof URLSearchParams
    ? currentSearch
    : new URLSearchParams(currentSearch || '');

  // Outside a server-confirmed owner preview, keep the pre-rollout behaviour
  // byte for byte: Back to home drops every parameter except promo_code.
  // Widening this to preserve affiliate and tracking parameters is a real
  // improvement, but it changes behaviour for every visitor on the platform,
  // so it belongs in its own change rather than riding along with the preview
  // rollout.
  if (!preservePreviewContext) {
    const visitorParams = new URLSearchParams();
    const promoCode = current.get('promo_code');
    if (promoCode) visitorParams.set('promo_code', promoCode);
    return visitorParams;
  }

  // Owner preview only: keep the audience, device and anything else the URL
  // carried, dropping just the product route and the one-shot entry flag, so
  // Back returns to the hub without flipping the simulated audience.
  const next = new URLSearchParams(current.toString());
  PRODUCT_ROUTE_PARAMS.forEach(key => next.delete(key));
  next.delete(PREVIEW_ENTRY_PARAM);
  return next;
};
